import { useMemo, useRef, useState } from "react"

import { HeaderCellType, HeaderGroupCellType, TableProps } from "./types"
import { classNames } from "../../../../lib/classNames"
import { DragGhost, DragGhostProps } from "../ColumnDnd/components/DragGhost"
import { DragHandle } from "../ColumnDnd/components/DragHandle"
import { parseColumnKey } from "../ColumnDnd/hooks/resolveColumnDrop"
import { useColumnDragDrop } from "../ColumnDnd/hooks/useColumnDnd"
import { DropIndicator, DropSide } from "../DropIndicator/DropIndicator"
import { TableCheckboxCell } from "../TableCheckboxCell"
import { TableHeaderCell } from "../TableHeaderCell"
import { TableHeaderGroupCell } from "../TableHeaderGroupCell"
import { TableHeaderGroupRow } from "../TableHeaderGroupRow"
import { TableHeaderRow } from "../TableHeaderRow"
import { groupRowCheckboxEmptyKey } from "../useTable/useTableHeader"

type TableHeaderProps = Pick<TableProps, "header" | "cellHeight">

export function TableHeader({ header, cellHeight }: TableHeaderProps) {
  const onReorder = header.onReorder
  const headerRowRef = useRef<HTMLTableRowElement>(null)

  const [ghostData, setGhostData] = useState<Omit<DragGhostProps, "position">>()

  const dataHeaderCells = useMemo(
    () => header.row.cells.filter((c) => c.type === HeaderCellType.Header),
    [header]
  )

  const headerKeys = useMemo(
    () => dataHeaderCells.map((c) => c.key),
    [dataHeaderCells]
  )

  const keyToIndex = useMemo(() => {
    const map = new Map<string, number>()
    dataHeaderCells.forEach((c, i) => map.set(c.key, i))
    return map
  }, [dataHeaderCells])

  const drag = useColumnDragDrop(headerRowRef, headerKeys, onReorder)

  const content = (
    <>
      {header.groupRow && (
        <TableHeaderGroupRow>
          {header.groupRow.cells.map((cell) => {
            if (cell.type === HeaderGroupCellType.Empty) {
              const isCheckboxCell = cell.key === groupRowCheckboxEmptyKey
              return (
                <TableHeaderGroupCell
                  key={cell.key}
                  colSpan={cell.colSpan}
                  height={cellHeight}
                  className={classNames(isCheckboxCell && "border-r-0")}
                />
              )
            }
            return (
              <TableHeaderGroupCell
                key={cell.key}
                colSpan={cell.colSpan}
                height={cellHeight}
                isExpandable={cell.isExpandable}
                isExpanded={cell.isExpanded}
                onToggleExpanded={cell.onToggleExpanded}
                title={cell.title}
                accessory={cell.accessory}
              />
            )
          })}
        </TableHeaderGroupRow>
      )}

      <TableHeaderRow
        ref={headerRowRef}
        className={classNames(
          drag.state === "dragging" && "bg-elevation-surface-accent-default"
        )}
      >
        {header.row.cells.map((cell) => {
          if (cell.type === HeaderCellType.Empty) {
            return (
              <TableHeaderCell
                key={cell.key}
                height={cellHeight}
                className="border-r-0"
                hideColumnBorders={header.hideColumnBorders}
              />
            )
          }

          if (cell.type === HeaderCellType.Checkbox) {
            return (
              <TableCheckboxCell
                key={cell.key}
                as="th"
                isChecked={cell.isChecked}
                onChangeIsChecked={({ isChecked }) =>
                  cell.onChangeIsChecked(isChecked)
                }
              />
            )
          }

          const idx = keyToIndex.get(cell.key)
          if (idx === undefined) {
            return (
              <TableHeaderCell
                key={cell.key}
                height={cellHeight}
                hideColumnBorders={header.hideColumnBorders}
              />
            )
          }

          let dropSide: DropSide | undefined = undefined
          if (drag.dropIndex === idx) {
            dropSide = "left"
          } else if (drag.dropIndex === idx + 1) {
            dropSide = "right"
          } else if (
            drag.dropIndex === dataHeaderCells.length &&
            idx === dataHeaderCells.length - 1
          ) {
            dropSide = "right"
          }

          return (
            <TableHeaderCell
              key={cell.key}
              height={cellHeight}
              data-column-index={idx}
              className={classNames(
                "group relative",
                drag.dragIndex === idx && "opacity-50"
              )}
              onStartResize={(e) => header.onStartColumnResize?.(cell.key, e)}
              onToggleColumnHideShow={cell.onToggleColumnHideShow}
              hideColumnBorders={header.hideColumnBorders}
            >
              {dataHeaderCells.length > 1 && (
                <>
                  {dropSide && <DropIndicator side={dropSide} />}
                  {header.onReorder &&
                    header.enableColumnReordering &&
                    drag.state !== "dragging" && (
                      <DragHandle
                        onMouseDown={(e) => {
                          const { colTitle } = parseColumnKey(cell.key)
                          const cellElement = e.currentTarget.closest("th")
                          if (!cellElement) {
                            return
                          }
                          const { offsetWidth, offsetHeight } = cellElement

                          setGhostData({
                            title: colTitle,
                            size: {
                              width: offsetWidth,
                              height: offsetHeight,
                            },
                          })
                          drag.handleMouseDown(idx)(e)
                        }}
                      />
                    )}
                </>
              )}

              <div className="relative flex items-center justify-center">
                <div className="w-full">{cell.content}</div>
              </div>
            </TableHeaderCell>
          )
        })}
      </TableHeaderRow>
    </>
  )

  return (
    <>
      {content}
      {ghostData && drag.state === "dragging" && drag.position && (
        <DragGhost
          position={drag.position}
          title={ghostData.title}
          size={ghostData.size}
        />
      )}
    </>
  )
}
